import React from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import axios from 'axios';

// Components
import Login from './components/auth/Login';
import SetupPassword from './components/auth/SetupPassword';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import PrivacyPolicy from './components/auth/PrivacyPolicy';
import Terms from './components/auth/Terms';
import Dashboard from './components/dashboard/Dashboard';
import Classes from './components/classes/Classes';
import Profile from './components/profile/Profile';
import NotificationSettings from './components/notifications/NotificationSettings';

import ManageUsers from './components/admin/ManageUsers';
import ManageClasses from './components/admin/ManageClasses';
import DataCleanup from './components/admin/DataCleanup';
import SurveyResults from './components/admin/SurveyResults';
import SurveyEditor from './components/admin/SurveyEditor';
import SurveyPage from './components/survey/SurveyPage';

import Layout from './components/layout/Layout';
import StagingBanner from './components/common/StagingBanner';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { DarkModeProvider } from './context/DarkModeContext';

// Styles
import './App.css';
import './styles/dark-mode.css';
import './styles/staging-banner.css';

// Configure axios defaults
const getApiBaseURL = () => {
  // Check for staging environment first (since NODE_ENV is always 'production' in builds)
  if (window.location.hostname === 'staging.fitflow.younitefitness.co.nz') {
    return 'https://staging.fitflow.younitefitness.co.nz';
  } else if (window.location.hostname === 'fitflow.younitefitness.co.nz') {
    return 'https://fitflow.younitefitness.co.nz';
  } else if (window.location.port === '3002') {
    // Local staging server
    return 'http://localhost:3002';
  } else {
    return 'http://localhost:3001';
  }
};

axios.defaults.baseURL = getApiBaseURL();

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, fullAdminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && !user.hasAdminAccess()) {
    return <Navigate to="/dashboard" replace />;
  }
  
  if (fullAdminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Dynamic Admin Component
const AdminUsersComponent = () => {
  return <ManageUsers />;
};

const AdminClassesComponent = () => {
  return <ManageClasses />;
};

// App Routes Component
const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/setup-password" element={<SetupPassword />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/survey" element={<SurveyPage />} />
      <Route path="/survey/:slug" element={<SurveyPage />} />
      
      {/* Protected Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="classes" element={<Classes />} />
        <Route path="profile" element={<Profile />} />
        <Route path="notification-settings" element={<NotificationSettings />} />
        
        {/* Admin Routes */}
        <Route path="admin/users" element={
          <ProtectedRoute adminOnly>
            <AdminUsersComponent />
          </ProtectedRoute>
        } />
        <Route path="admin/classes" element={
          <ProtectedRoute adminOnly>
            <AdminClassesComponent />
          </ProtectedRoute>
        } />
        <Route path="admin/stats" element={
          <ProtectedRoute fullAdminOnly>
            <DataCleanup />
          </ProtectedRoute>
        } />
        <Route path="admin/surveys" element={
          <ProtectedRoute adminOnly>
            <SurveyResults />
          </ProtectedRoute>
        } />
        <Route path="admin/surveys/new" element={
          <ProtectedRoute adminOnly>
            <SurveyEditor />
          </ProtectedRoute>
        } />
        <Route path="admin/surveys/:id/edit" element={
          <ProtectedRoute adminOnly>
            <SurveyEditor />
          </ProtectedRoute>
        } />

      </Route>
      
      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

// Main App Component
function App() {
  return (
    <DarkModeProvider>
      <AuthProvider>
        <NotificationProvider>
          <BrowserRouter>
            <div className="App">
              <StagingBanner />
              <AppRoutes />
              <Toaster
              position="bottom-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                  fontSize: '14px',
                  bottom: '20px',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#10B981',
                    secondary: '#fff',
                },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#EF4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </div>
          </BrowserRouter>
        </NotificationProvider>
      </AuthProvider>
    </DarkModeProvider>
  );
}

export default App;
